import React from 'react';

const cardStyle = {
    backgroundColor: '#2d2d2d',
    border: '1px solid #404040',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
};

const sectionTitleStyle = {
    marginBottom: '15px',
    color: '#e0e0e0',
    fontWeight: '600'
};

const labelStyle = {
    fontSize: '12px',
    color: '#a0a0a0',
    textTransform: 'uppercase',
    marginBottom: '6px',
    letterSpacing: '0.4px',
    fontWeight: '600'
};

const valueStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#f5f5f5'
};

const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '12px',
    backgroundColor: '#3a3a3a',
    color: '#e0e0e0',
    fontSize: '12px',
    marginRight: '8px',
    marginBottom: '8px',
    border: '1px solid #4a4a4a'
};

const SectionCard = ({ title, children, style }) => (
    <div style={{ ...cardStyle, ...style }}>
        {title && (
            <h5 style={{ margin: '0 0 12px', color: '#e0e0e0', fontWeight: 600 }}>
                {title}
            </h5>
        )}
        {children}
    </div>
);

const InlineCssPreview = ({ inlineCss }) => {
    if (!inlineCss.length) return null;

    return (
        <SectionCard title="Inline CSS">
            <div style={{ display: 'grid', gap: '10px' }}>
                {inlineCss.map((css, index) => {
                    const snippet = css.length > 600 ? `${css.slice(0, 600)}…` : css;
                    return (
                        <div key={index}>
                            <div style={labelStyle}>Block {index + 1}</div>
                            <pre style={{
                                backgroundColor: '#1f1f1f',
                                borderRadius: '6px',
                                padding: '12px',
                                maxHeight: '220px',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                margin: 0,
                                color: '#d8d8d8',
                                fontSize: '12px',
                                lineHeight: 1.5,
                                border: '1px solid #2f2f2f'
                            }}>
                                {snippet}
                            </pre>
                        </div>
                    );
                })}
            </div>
        </SectionCard>
    );
};

const CssUrls = ({ cssUrls }) => {
    if (!cssUrls.length) return null;

    return (
        <SectionCard title="CSS URLs">
            <div style={{ display: 'grid', gap: '8px' }}>
                {cssUrls.map((url, index) => (
                    <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color: '#7dd3fc',
                            textDecoration: 'none',
                            wordBreak: 'break-all'
                        }}
                    >
                        {url}
                    </a>
                ))}
            </div>
        </SectionCard>
    );
};

const CssVariables = ({ cssVariables }) => {
    const entries = Object.entries(cssVariables || {});
    if (entries.length === 0) return null;

    return (
        <SectionCard title="CSS Variables">
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '10px'
            }}>
                {entries.map(([key, value]) => (
                    <div
                        key={key}
                        style={{
                            backgroundColor: '#1f1f1f',
                            borderRadius: '6px',
                            padding: '10px',
                            border: '1px solid #2f2f2f'
                        }}
                    >
                        <div style={{ ...labelStyle, marginBottom: '4px' }}>{key}</div>
                        <div style={{ fontSize: '13px', color: '#e5e5e5' }}>{String(value)}</div>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
};

const ColorPalette = ({ colors }) => {
    const values = colors?.values || [];
    const frequency = colors?.frequency || {};
    if (!values.length) return null;

    return (
        <SectionCard title="Colors">
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px'
            }}>
                {values.map((color, index) => (
                    <div
                        key={`${color}-${index}`}
                        style={{
                            ...cardStyle,
                            padding: '12px',
                            backgroundColor: '#1f1f1f',
                            border: '1px solid #2f2f2f'
                        }}
                    >
                        <div style={{
                            width: '100%',
                            height: '40px',
                            borderRadius: '6px',
                            background: color,
                            border: '1px solid #3a3a3a',
                            marginBottom: '8px'
                        }} />
                        <div style={{ color: '#f5f5f5', fontSize: '13px', fontWeight: 600 }}>
                            {color}
                        </div>
                        {frequency[color] ? (
                            <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                                Used {frequency[color]} time{frequency[color] > 1 ? 's' : ''}
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        </SectionCard>
    );
};

const Typography = ({ typography }) => {
    const {
        font_families = [],
        font_sizes = [],
        font_weights = [],
        line_heights = []
    } = typography || {};

    if (!font_families.length && !font_sizes.length && !font_weights.length && !line_heights.length) {
        return null;
    }

    return (
        <SectionCard title="Typography">
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '12px'
            }}>
                {font_families.length > 0 && (
                    <div>
                        <div style={labelStyle}>Font Families</div>
                        {font_families.map((font) => (
                            <div key={font} style={chipStyle}>{font}</div>
                        ))}
                    </div>
                )}
                {font_sizes.length > 0 && (
                    <div>
                        <div style={labelStyle}>Font Sizes</div>
                        {font_sizes.map((size) => (
                            <div key={size} style={chipStyle}>{size}</div>
                        ))}
                    </div>
                )}
                {font_weights.length > 0 && (
                    <div>
                        <div style={labelStyle}>Font Weights</div>
                        {font_weights.map((weight) => (
                            <div key={weight} style={chipStyle}>{weight}</div>
                        ))}
                    </div>
                )}
                {line_heights.length > 0 && (
                    <div>
                        <div style={labelStyle}>Line Heights</div>
                        {line_heights.map((lh) => (
                            <div key={lh} style={chipStyle}>{lh}</div>
                        ))}
                    </div>
                )}
            </div>
        </SectionCard>
    );
};

const Spacing = ({ spacing }) => {
    const values = spacing?.values || [];
    if (!values.length) return null;

    return (
        <SectionCard title="Spacing">
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {values.map((space, index) => (
                    <div key={`${space}-${index}`} style={chipStyle}>{space}</div>
                ))}
            </div>
        </SectionCard>
    );
};

const Effects = ({ effects }) => {
    const { border_radius = [], box_shadows = [], gradients = [], transitions = [] } = effects || {};
    if (!border_radius.length && !box_shadows.length && !gradients.length && !transitions.length) return null;

    return (
        <SectionCard title="Effects">
            <div style={{ display: 'grid', gap: '10px' }}>
                {border_radius.length > 0 && (
                    <div>
                        <div style={labelStyle}>Border Radius</div>
                        {border_radius.map((val, index) => (
                            <div key={`${val}-${index}`} style={chipStyle}>{val}</div>
                        ))}
                    </div>
                )}
                {box_shadows.length > 0 && (
                    <div>
                        <div style={labelStyle}>Box Shadows</div>
                        {box_shadows.map((val, index) => (
                            <div key={`${val}-${index}`} style={chipStyle}>{val}</div>
                        ))}
                    </div>
                )}
                {gradients.length > 0 && (
                    <div>
                        <div style={labelStyle}>Gradients</div>
                        {gradients.map((val, index) => (
                            <div key={`${val}-${index}`} style={chipStyle}>{val}</div>
                        ))}
                    </div>
                )}
                {transitions.length > 0 && (
                    <div>
                        <div style={labelStyle}>Transitions</div>
                        {transitions.map((val, index) => (
                            <div key={`${val}-${index}`} style={chipStyle}>{val}</div>
                        ))}
                    </div>
                )}
            </div>
        </SectionCard>
    );
};

const Layout = ({ layout }) => {
    const { breakpoints = [], container_max_widths = [] } = layout || {};
    if (!breakpoints.length && !container_max_widths.length) return null;

    return (
        <SectionCard title="Layout">
            <div style={{ display: 'grid', gap: '10px' }}>
                {breakpoints.length > 0 && (
                    <div>
                        <div style={labelStyle}>Breakpoints</div>
                        {breakpoints.map((bp) => (
                            <div key={bp} style={chipStyle}>{bp}</div>
                        ))}
                    </div>
                )}
                {container_max_widths.length > 0 && (
                    <div>
                        <div style={labelStyle}>Container Max Widths</div>
                        {container_max_widths.map((w) => (
                            <div key={w} style={chipStyle}>{w}</div>
                        ))}
                    </div>
                )}
            </div>
        </SectionCard>
    );
};

const ComponentsOverview = ({ components }) => {
    if (!components || typeof components !== 'object') return null;

    const { headings = {}, body, buttons = [] } = components;
    const headingEntries = Object.entries(headings);

    if (headingEntries.length === 0 && !body && !buttons.length) {
        return null;
    }

    return (
        <SectionCard title="Components">
            <div style={{ display: 'grid', gap: '12px' }}>
                {headingEntries.length > 0 && (
                    <div>
                        <div style={labelStyle}>Headings</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '10px'
                        }}>
                            {headingEntries.map(([level, config]) => (
                                <div key={level} style={{
                                    backgroundColor: '#1f1f1f',
                                    borderRadius: '6px',
                                    padding: '10px',
                                    border: '1px solid #2f2f2f'
                                }}>
                                    <div style={{ ...labelStyle, marginBottom: '4px' }}>{level.toUpperCase()}</div>
                                    <div style={{ color: '#f5f5f5', fontSize: '13px', lineHeight: 1.5 }}>
                                        {config.fontSize && <div>Size: {config.fontSize}</div>}
                                        {config.fontWeight && <div>Weight: {config.fontWeight}</div>}
                                        {config.lineHeight && <div>Line Height: {config.lineHeight}</div>}
                                        {config.fontFamily && <div>Font: {config.fontFamily}</div>}
                                        {config.color && <div>Color: {config.color}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {body && (
                    <div>
                        <div style={labelStyle}>Body</div>
                        <div style={{
                            backgroundColor: '#1f1f1f',
                            borderRadius: '6px',
                            padding: '10px',
                            border: '1px solid #2f2f2f',
                            color: '#f5f5f5',
                            fontSize: '13px',
                            lineHeight: 1.6
                        }}>
                            {body.fontSize && <div>Size: {body.fontSize}</div>}
                            {body.fontWeight && <div>Weight: {body.fontWeight}</div>}
                            {body.lineHeight && <div>Line Height: {body.lineHeight}</div>}
                            {body.fontFamily && <div>Font: {body.fontFamily}</div>}
                            {body.color && <div>Color: {body.color}</div>}
                        </div>
                    </div>
                )}

                {buttons.length > 0 && (
                    <div>
                        <div style={labelStyle}>Buttons</div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '10px'
                        }}>
                            {buttons.map((btn, index) => (
                                <div key={index} style={{
                                    backgroundColor: '#1f1f1f',
                                    borderRadius: '6px',
                                    padding: '10px',
                                    border: '1px solid #2f2f2f',
                                    color: '#f5f5f5',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}>
                                    {btn.backgroundColor && <div>Background: {btn.backgroundColor}</div>}
                                    {btn.color && <div>Color: {btn.color}</div>}
                                    {btn.borderRadius && <div>Radius: {btn.borderRadius}</div>}
                                    {btn.padding && <div>Padding: {btn.padding}</div>}
                                    {btn.fontSize && <div>Size: {btn.fontSize}</div>}
                                    {btn.fontWeight && <div>Weight: {btn.fontWeight}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </SectionCard>
    );
};

const CSSSection = ({ cssData }) => {
    if (!cssData) return null;

    const {
        inline_css = [],
        css_urls = [],
        css_variables = {},
        colors = {},
        typography = {},
        spacing = {},
        effects = {},
        layout = {},
        components = {},
        external_css = {}
    } = cssData || {};

    const summaryItems = [
        {
            label: 'Inline CSS blocks',
            value: inline_css.length,
            hint: `${inline_css.reduce((sum, css) => sum + (css?.length || 0), 0)} chars total`
        },
        {
            label: 'CSS URLs',
            value: css_urls.length,
            hint: external_css ? `${Object.keys(external_css).length} external` : null
        },
        {
            label: 'Variables',
            value: Object.keys(css_variables || {}).length,
            hint: 'custom properties'
        },
        {
            label: 'Colors',
            value: (colors.values || []).length,
            hint: 'unique values'
        }
    ];

    return (
        <div style={{ marginBottom: '30px' }}>
            <h4 style={sectionTitleStyle}>CSS</h4>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
                marginBottom: '16px'
            }}>
                {summaryItems.map((item, index) => (
                    <div key={index} style={cardStyle}>
                        <div style={labelStyle}>{item.label}</div>
                        <div style={valueStyle}>{item.value}</div>
                        {item.hint && <div style={{ color: '#9ca3af', fontSize: '12px' }}>{item.hint}</div>}
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
                <InlineCssPreview inlineCss={inline_css} />
                <CssUrls cssUrls={css_urls} />
                <CssVariables cssVariables={css_variables} />
                <ColorPalette colors={colors} />
                <Typography typography={typography} />
                <Spacing spacing={spacing} />
                <Effects effects={effects} />
                <Layout layout={layout} />
                <ComponentsOverview components={components} />
            </div>
        </div>
    );
};

export default CSSSection;



